import { useEffect, useRef } from "react";

/* Cinematic hero fireworks for a side-panel composition.
 * Opening sequence (~2–3s): a launch trail rises, a large gold firework bursts and
 * briefly illuminates the smoke, sparks drift, then it settles into a calm ambient loop
 * (one elegant burst every 6–10s + smaller distant bursts + floating gold dust + soft smoke).
 * Warm gold/amber glow with occasional red accents. GPU-friendly single 2D canvas,
 * reduced counts on mobile, pauses when hidden, honours prefers-reduced-motion. */
export default function FireworksCanvas({ className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isMobile = window.innerWidth < 768;

    let w = 0, h = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      w = canvas.clientWidth; h = canvas.clientHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const GOLD = ["#E8B54B", "#F5A623", "#FFE9B0", "#F7D98A"];
    const ACCENT = ["#D23B2E", "#F2761B"];
    const RARE = ["#5B8DEF", "#EAF2FF"];
    const rockets = [];
    const sparks = [];
    const flashes = [];
    const dust = [];

    const dustCount = reduce ? 20 : isMobile ? 26 : 52;
    for (let i = 0; i < dustCount; i++) {
      dust.push({ x: Math.random() * w, y: Math.random() * h, r: Math.random() * 1.7 + 0.4, vy: -(Math.random() * 0.2 + 0.05), vx: (Math.random() - 0.5) * 0.15, a: Math.random() * 0.5 + 0.2, tw: Math.random() * Math.PI * 2 });
    }

    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

    const launch = (kind = "normal") => {
      const cx = kind === "opening" ? w * (0.55 + Math.random() * 0.2) : w * (0.28 + Math.random() * 0.55);
      const accent = kind !== "opening" && Math.random() < 0.28;
      rockets.push({
        x: cx, y: h,
        tx: cx + (Math.random() - 0.5) * 40,
        ty: kind === "opening" ? h * 0.34 : kind === "distant" ? h * (0.34 + Math.random() * 0.24) : h * (0.18 + Math.random() * 0.26),
        vy: -(kind === "opening" ? 9 : kind === "distant" ? 5.5 : 7.2),
        color: accent ? pick(ACCENT) : (kind !== "opening" && Math.random() < 0.12 ? pick(RARE) : pick(GOLD)),
        kind,
      });
    };

    const burst = (x, y, color, kind) => {
      const base = isMobile ? 30 : 54;
      const n = kind === "opening" ? Math.floor(base * 1.7) : kind === "distant" ? Math.floor(base * 0.5) : base;
      const power = kind === "distant" ? 1.6 : kind === "opening" ? 4.2 : 3;
      const decayBase = kind === "opening" ? 0.0028 : kind === "distant" ? 0.016 : 0.007;
      const decayRand = kind === "opening" ? 0.0022 : 0.012;
      for (let i = 0; i < n; i++) {
        const ang = (Math.PI * 2 * i) / n + Math.random() * 0.2;
        const spd = Math.random() * power + (kind === "distant" ? 0.6 : 1.4);
        sparks.push({ x, y, vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd, life: 1, decay: Math.random() * decayRand + decayBase, color: Math.random() > 0.55 ? color : pick(GOLD), r: (Math.random() * 2 + 0.9) * (kind === "distant" ? 0.7 : kind === "opening" ? 1.15 : 1) });
      }
      flashes.push({ x, y, r: 4, max: kind === "opening" ? 360 : kind === "distant" ? 90 : 190, life: 1, decay: kind === "opening" ? 0.008 : 0.022, color });
    };

    let raf, running = true;
    let nextLaunch = performance.now() + 3000;
    let nextDistant = performance.now() + 1400;
    let opened = false;

    const frame = (t) => {
      if (!running) return;
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "rgba(5,7,13,0.20)";
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = "lighter";

      // Illumination flashes (light up smoke)
      for (let i = flashes.length - 1; i >= 0; i--) {
        const f = flashes[i];
        f.r += (f.max - f.r) * 0.08; f.life -= f.decay;
        if (f.life <= 0) { flashes.splice(i, 1); continue; }
        const g = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.r);
        g.addColorStop(0, `rgba(245,190,90,${0.20 * f.life})`);
        g.addColorStop(0.5, `rgba(232,181,75,${0.08 * f.life})`);
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2); ctx.fill();
      }

      // Floating gold dust
      for (const d of dust) {
        d.y += d.vy; d.x += d.vx; d.tw += 0.02;
        if (d.y < -5) { d.y = h + 5; d.x = Math.random() * w; }
        const flick = 0.6 + Math.sin(d.tw) * 0.4;
        ctx.beginPath();
        ctx.fillStyle = `rgba(232,181,75,${d.a * flick})`;
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fill();
      }

      if (!reduce) {
        if (!opened) { burst(w * 0.66, h * 0.36, "#F5C563", "opening"); launch("normal"); opened = true; }
        if (opened && t > nextLaunch && rockets.length < (isMobile ? 2 : 3)) {
          launch("normal");
          nextLaunch = t + (4000 + Math.random() * 3000);
        }
        if (opened && !isMobile && t > nextDistant && rockets.length < 4) {
          launch("distant");
          nextDistant = t + (3000 + Math.random() * 3200);
        }
        for (let i = rockets.length - 1; i >= 0; i--) {
          const r = rockets[i];
          r.x += (r.tx - r.x) * 0.02; r.y += r.vy; r.vy += 0.06;
          ctx.beginPath();
          ctx.fillStyle = r.color;
          ctx.arc(r.x, r.y, r.kind === "distant" ? 1.4 : 2.3, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "rgba(245,166,35,0.3)";
          ctx.lineWidth = 1.3;
          ctx.beginPath(); ctx.moveTo(r.x, r.y); ctx.lineTo(r.x, r.y + 15); ctx.stroke();
          if (r.y <= r.ty || r.vy >= -0.5) { burst(r.x, r.y, r.color, r.kind); rockets.splice(i, 1); }
        }
        for (let i = sparks.length - 1; i >= 0; i--) {
          const s = sparks[i];
          s.x += s.vx; s.y += s.vy; s.vy += 0.05; s.vx *= 0.985; s.life -= s.decay;
          if (s.life <= 0) { sparks.splice(i, 1); continue; }
          ctx.globalAlpha = Math.max(s.life, 0);
          ctx.beginPath();
          ctx.fillStyle = s.color;
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      }

      raf = requestAnimationFrame(frame);
    };

    raf = requestAnimationFrame(frame);

    const onVis = () => {
      running = !document.hidden;
      if (running) raf = requestAnimationFrame(frame);
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
